import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import {
	$canvasRuntime,
	type CanvasRuntimeParams,
	type Disposable,
} from "../../internal/canvas-runtime";
import { useMutationBuilder } from "./useMutationBuilder";
import { useQueryBuilder } from "./useQueryBuilder";

/**
 * Hook to access and manage a canvas runtime for a document/page.
 *
 * This hook:
 * - Creates the runtime on first mount if it doesn't exist
 * - Returns the existing runtime if already created
 * - Automatically disposes the runtime when the component unmounts and no other
 *   components are using it (via Jotai's garbage collection)
 *
 * The runtime is cached by documentId:pageId, so multiple components can share
 * the same runtime instance.
 *
 * @param params - The runtime parameters (documentId, pageId, documentInteractionMode)
 * @returns The canvas runtime instance, or null if not yet created
 */
export const useCanvasRuntime = <T extends Disposable>(params: CanvasRuntimeParams): T | null => {
	const { documentId, pageId, documentInteractionMode } = params;

	// Get the query builder for this document
	const query = useQueryBuilder(documentId);

	// Get the mutation builder for this document
	const mutation = useMutationBuilder(documentId);

	// Get the setter for the runtime atom
	const setRuntime = useSetAtom($canvasRuntime({ documentId, pageId }));

	// Get the current runtime value
	const runtime = useAtomValue($canvasRuntime({ documentId, pageId }));

	// Initialize the runtime on mount
	useEffect(() => {
		setRuntime({
			documentId,
			pageId,
			documentInteractionMode,
			query,
			mutation,
		});
	}, [setRuntime, documentId, pageId, documentInteractionMode, query, mutation]);

	return runtime as T | null;
};

/**
 * Hook to get an existing canvas runtime without creating a new one.
 *
 * This is useful when you want to access a runtime that should already exist
 * without triggering creation.
 *
 * @param documentId - The document ID
 * @param pageId - The page ID
 * @returns The canvas runtime instance, or null if not created
 */
export const useExistingCanvasRuntime = <T extends Disposable>(
	documentId: string,
	pageId: string
): T | null => {
	const runtime = useAtomValue($canvasRuntime({ documentId, pageId }));
	return runtime as T | null;
};
